/**
 * Notification Utility Functions
 */

import {
  BellOutlined,
  MailOutlined,
  CalendarOutlined,
  TeamOutlined,
  SettingOutlined,
  FileTextOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import type { Notification } from '@/api/types/notification.types';

/**
 * Get icon component for notification category
 */
export function getNotificationIcon(category?: string | null) {
  switch (category) {
    case 'Notifications.notification_category.TICKET':
      return FileTextOutlined;
    case 'Notifications.notification_category.CHAT':
      return MailOutlined;
    case 'Notifications.notification_category.CALENDAR':
      return CalendarOutlined;
    case 'Notifications.notification_category.USER':
      return TeamOutlined;
    case 'Notifications.notification_category.SYSTEM':
      return SettingOutlined;
    case 'Notifications.notification_category.LEAD':
      return DollarOutlined;
    default:
      return BellOutlined;
  }
}

/**
 * Get color for notification priority
 */
export function getNotificationColor(priority: string): string {
  switch (priority) {
    case 'Notifications.notification_priority.URGENT':
      return '#ff4d4f'; // Red
    case 'Notifications.notification_priority.HIGH':
      return '#faad14'; // Orange
    case 'Notifications.notification_priority.NORMAL':
      return '#1890ff'; // Blue
    case 'Notifications.notification_priority.LOW':
      return '#52c41a'; // Green
    default:
      return '#8c8c8c'; // Gray
  }
}

/**
 * Format timestamp as relative time (e.g., "2 minutes ago")
 */
export function formatTimestamp(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return 'Just now';
  if (minutes < 60)
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

/**
 * Play notification sound using Web Audio API
 * @param soundFile - Sound file name (e.g., 'ding.mp3', 'chime.mp3', 'urgent.mp3')
 */
export async function playNotificationSound(soundFile: string = 'ding.mp3'): Promise<void> {
  const { playSoundByFilename } = await import('./sound');
  await playSoundByFilename(soundFile);
}

/**
 * Group notifications by category
 */
export function groupNotificationsByCategory(
  notifications: Notification[]
): Record<string, Notification[]> {
  return notifications.reduce(
    (groups, notification) => {
      const category = notification.category || 'OTHER';
      if (!groups[category]) groups[category] = [];
      groups[category].push(notification);
      return groups;
    },
    {} as Record<string, Notification[]>
  );
}

/**
 * Get human-readable label for notification category
 */
export function getCategoryLabel(category: string): string {
  switch (category) {
    case 'Notifications.notification_category.SYSTEM':
      return 'System';
    case 'Notifications.notification_category.USER':
      return 'User';
    case 'Notifications.notification_category.TICKET':
      return 'Tickets';
    case 'Notifications.notification_category.LEAD':
      return 'Leads';
    case 'Notifications.notification_category.CHAT':
      return 'Chat';
    case 'Notifications.notification_category.CALENDAR':
      return 'Calendar';
    case 'Notifications.notification_category.OTHER':
      return 'Other';
    default:
      return category;
  }
}

/**
 * Get human-readable label for notification priority
 */
export function getPriorityLabel(priority: string): string {
  switch (priority) {
    case 'Notifications.notification_priority.URGENT':
      return 'Urgent';
    case 'Notifications.notification_priority.HIGH':
      return 'High';
    case 'Notifications.notification_priority.NORMAL':
      return 'Normal';
    case 'Notifications.notification_priority.LOW':
      return 'Low';
    default:
      return priority;
  }
}
